import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { doc, onSnapshot, collection, query, where } from "firebase/firestore";
import { db } from "../../../Firebase";
import styled, { createGlobalStyle, keyframes } from "styled-components";
import { V3Room, V3Event, V3User, V3Submission } from "../types/v3types";

const GlobalStyle = createGlobalStyle`
  * {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
  }
  
  body {
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen',
      'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue',
      sans-serif;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    overflow: hidden;
  }
`;

const Container = styled.div`
  width: 100vw;
  height: 100vh;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const pulse = keyframes`
  0%, 100% { opacity: 0.3; transform: scale(1); }
  50% { opacity: 0.6; transform: scale(1.05); }
`;

const BackgroundAnimation = styled.div`
  position: absolute;
  top: -50%;
  left: -50%;
  width: 200%;
  height: 200%;
  background: radial-gradient(circle, rgba(255,255,255,0.1) 1px, transparent 1px);
  background-size: 50px 50px;
  animation: ${pulse} 4s ease-in-out infinite;
`;

const Content = styled.div`
  width: 95%;
  height: 90%;
  position: relative;
  z-index: 1;
`;

const RankingContainer = styled.div`
  width: 100%;
  height: 100%;
  background: rgba(255, 255, 255, 0.95);
  border: 4px solid #dc143c;
  border-radius: 20px;
  padding: 30px 40px;
  overflow: hidden;
  box-shadow: 0 10px 40px rgba(220, 20, 60, 0.4);
  display: flex;
  flex-direction: column;
`;

const RankingTitle = styled.h1`
  font-size: 56px;
  text-align: center;
  margin-bottom: 30px;
  color: #dc143c;
  font-weight: bold;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.1);
`;

const RankingGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 40px;
  flex: 1;
  overflow: hidden;
`;

const RankingColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 15px;
`;

const slideIn = keyframes`
  from { transform: translateX(-100%); opacity: 0; }
  to { transform: translateX(0); opacity: 1; }
`;

const RankingItem = styled.div<{ rank: number }>`
  display: flex;
  align-items: center;
  padding: 20px 30px;
  background: ${props => {
    if (props.rank === 1) return "linear-gradient(135deg, #ffd700, #ffed4b)";
    if (props.rank === 2) return "linear-gradient(135deg, #c0c0c0, #e8e8e8)";
    if (props.rank === 3) return "linear-gradient(135deg, #cd7f32, #e6a857)";
    return "#f8f9fa";
  }};
  border-radius: 15px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
  animation: ${slideIn} 0.5s ease-out ${props => props.rank * 0.1}s both;
  border: ${props => props.rank <= 3 ? "3px solid rgba(255,255,255,0.5)" : "2px solid #e0e0e0"};
`;

const RankNumber = styled.span<{ rank: number }>`
  font-size: ${props => props.rank <= 3 ? "36px" : "32px"};
  font-weight: bold;
  color: ${props => props.rank <= 3 ? "#fff" : "#333"};
  width: 60px;
  text-align: center;
  text-shadow: ${props => props.rank <= 3 ? "2px 2px 4px rgba(0,0,0,0.3)" : "none"};
`;

const RankName = styled.span<{ rank: number }>`
  flex: 1;
  font-size: ${props => props.rank <= 3 ? "28px" : "24px"};
  font-weight: ${props => props.rank <= 3 ? "bold" : "normal"};
  color: ${props => props.rank <= 3 ? "#fff" : "#333"};
  margin-left: 20px;
  text-shadow: ${props => props.rank <= 3 ? "1px 1px 2px rgba(0,0,0,0.3)" : "none"};
`;

const RankScore = styled.span<{ rank: number }>`
  font-size: ${props => props.rank <= 3 ? "28px" : "24px"};
  font-weight: bold;
  color: ${props => props.rank <= 3 ? "#fff" : "#dc143c"};
  text-shadow: ${props => props.rank <= 3 ? "1px 1px 2px rgba(0,0,0,0.3)" : "none"};
`;

const LoadingScreen = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  font-size: 36px;
  color: #fff;
  text-shadow: 2px 2px 4px rgba(0,0,0,0.3);
`;

const V3RankingScreen: React.FC = () => {
  const { projectId, roomId } = useParams<{ projectId: string; roomId: string }>();
  const [room, setRoom] = useState<V3Room | null>(null);
  const [event, setEvent] = useState<V3Event | null>(null);
  const [participants, setParticipants] = useState<V3User[]>([]);
  const [submissions, setSubmissions] = useState<V3Submission[]>([]);

  useEffect(() => {
    if (!roomId || !projectId) return;

    // Subscribe to room updates
    const unsubscribeRoom = onSnapshot(
      doc(db, "v3rooms", roomId),
      (doc) => {
        if (doc.exists()) {
          setRoom(doc.data() as V3Room);
        }
      }
    );

    return () => {
      unsubscribeRoom();
    };
  }, [roomId, projectId]);

  useEffect(() => {
    if (!room) return;

    // Subscribe to event
    const unsubscribeEvent = onSnapshot(
      doc(db, "v3events", room.eventId),
      (doc) => {
        if (doc.exists()) {
          setEvent(doc.data() as V3Event);
        }
      }
    );

    // Subscribe to participants
    const participantIds = room.participants || [];
    if (participantIds.length > 0) {
      const unsubscribes = participantIds.map((userId) =>
        onSnapshot(doc(db, "v3users", userId), (doc) => {
          if (doc.exists()) {
            const userData = doc.data() as V3User;
            setParticipants((prev) => {
              const filtered = prev.filter((u) => u.userId !== userData.userId);
              return [...filtered, userData];
            });
          }
        })
      );

      return () => {
        unsubscribeEvent();
        unsubscribes.forEach((unsub) => unsub());
      };
    }

    return () => {
      unsubscribeEvent();
    };
  }, [room]);

  useEffect(() => {
    if (!roomId) return;

    // Subscribe to submissions
    const q = query(
      collection(db, "v3submissions"),
      where("roomId", "==", roomId)
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const subs: V3Submission[] = [];
      snapshot.forEach((doc) => {
        subs.push(doc.data() as V3Submission);
      });
      setSubmissions(subs);
    });

    return () => unsubscribe();
  }, [roomId]);

  const calculateScore = (userId: string) => {
    return submissions
      .filter(sub => sub.userId === userId && sub.isCorrect)
      .reduce((total, sub) => total + sub.score + (sub.kachidokiPoints || 0), 0);
  };

  const getRanking = () => {
    return participants
      .map(user => ({
        user,
        score: calculateScore(user.userId)
      }))
      .sort((a, b) => b.score - a.score);
  };

  if (!room || !event) {
    return (
      <Container>
        <GlobalStyle />
        <BackgroundAnimation />
        <Content>
          <LoadingScreen>読み込み中...</LoadingScreen>
        </Content>
      </Container>
    );
  }

  const ranking = getRanking();
  const leftColumn = ranking.slice(0, 6);
  const rightColumn = ranking.slice(6, 12);

  return (
    <Container>
      <GlobalStyle />
      <BackgroundAnimation />
      <Content>
        <RankingContainer>
          <RankingTitle>解答者ランキング</RankingTitle>
          <RankingGrid>
            <RankingColumn>
              {leftColumn.map((item, index) => (
                <RankingItem key={item.user.userId} rank={index + 1}>
                  <RankNumber rank={index + 1}>{index + 1}</RankNumber>
                  <RankName rank={index + 1}>{item.user.username}</RankName>
                  <RankScore rank={index + 1}>{item.score.toFixed(3)}点</RankScore>
                </RankingItem>
              ))}
            </RankingColumn>
            <RankingColumn>
              {rightColumn.map((item, index) => (
                <RankingItem key={item.user.userId} rank={index + 7}>
                  <RankNumber rank={index + 7}>{index + 7}</RankNumber>
                  <RankName rank={index + 7}>{item.user.username}</RankName>
                  <RankScore rank={index + 7}>{item.score.toFixed(3)}点</RankScore>
                </RankingItem>
              ))}
            </RankingColumn>
          </RankingGrid>
        </RankingContainer>
      </Content>
    </Container>
  );
};

export default V3RankingScreen;